import * as React from "react";
import styled from "styled-components";
import { select, useSelect } from "@hubper/theme";
import { animated, useSpring } from "react-spring";

const BAR_HEIGHT = 16;
const CIRCLE_SIZE = 20;
const FACTOR = 1.75;
const WIDTH = CIRCLE_SIZE * FACTOR;
const RADIUS = CIRCLE_SIZE / 2;

const Container = styled.div`
  position: relative;
  width: ${WIDTH}px;
  height: ${CIRCLE_SIZE}px;
  cursor: pointer;
`;

const Bar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  border-radius: ${RADIUS}px;
  transform: translateY(-50%);
  height: ${BAR_HEIGHT}px;
  overflow: hidden;
  background-color: ${select.color(["grey", 2])};
`;

const InnerBar = styled(animated.div)`
  width: 100%;
  height: 100%;
  background-color: ${select.color(["primary", 1])};
`;

const Circle = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  box-shadow: 0px 1px 2px 0.5px rgba(0, 0, 0, 0.38);
  border-radius: ${RADIUS}px;
  width: ${CIRCLE_SIZE}px;
  height: ${CIRCLE_SIZE}px;
  background-color: white;
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  value?: boolean;
  onClick?: (value: boolean) => void;
};

function Toggle({ style, className, value, onClick }: Props, ref: any) {
  const { color } = useSelect();
  const [isOver, setOver] = React.useState();

  const circleStyle = useSpring({
    transform: `translateX(${value ? WIDTH / 2 - 2 : 0}px) scale(${
      isOver ? 1.05 : 1
    })`,
    backgroundColor: value ? color(["primary", 4]) : "white",
    config: {
      tension: 500,
      friction: 20,
      velocity: 10,
      mass: 1
    }
  });

  const barStyle = useSpring({
    transform: `translateX(${value ? 0 : -WIDTH}px)`,
    config: {
      tension: 400,
      friction: 30,
      mass: 1
    }
  });

  const handleClick = () => {
    if (onClick) {
      onClick(!value);
    }
  };

  return (
    <Container
      style={style}
      className={className}
      ref={ref}
      onClick={handleClick}
      onMouseEnter={() => setOver(true)}
      onMouseLeave={() => setOver(false)}
    >
      <Bar>
        <InnerBar style={barStyle} />
      </Bar>
      <Circle style={circleStyle} />
    </Container>
  );
}

export default React.forwardRef(Toggle);
